<template>
  <div ref="contextDialog" class="context-menu">
    <Tooltip
      v-if="!isDevice && tooltip"
      :align="tooltip.align"
      :custom-class="tooltip.customClass"
      :target-element="tooltip.targetElement"
    >
      <span v-html="tooltip.text" />
    </Tooltip>
    <Tooltip
      v-if="contextMenuTooltip"
      :align="contextMenuTooltip.align"
      :custom-class="contextMenuTooltip.customClass"
      :parent-element="parentElement"
      :target-element="contextMenuTooltip.targetElement"
      :x-offset-border="xOffsetBorder"
      :y-offset="yOffset"
      from-element-class="slider-component"
    >
      <div
        :class="contextMenuClass"
        class="context-menu-content"
        @click="menuIconClickHandler"
      >
        <SlideGroup
          ref="contextDialog"
          :lazy-load-items="menuItems.length"
          :list="menuItems"
          arrow-class="close-prevent"
          class="context-menu-slider"
        >
          <template #item="{ item }">
            <component
              :is="item.controlName"
              v-if="item.isVisible && item.id"
              :disabled="item.disabled"
              :icon-disabled="item.disableIcon"
              :test-id="item?.testId || null"
              @controlEvent="controlEventHandler"
            />
          </template>
        </SlideGroup>
      </div>
    </Tooltip>
  </div>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
import EditAnnotationControl from '@/components/controls/EditAnnotationControl/EditAnnotationControl';
import DeleteAnnotationControl from '@/components/controls/DeleteAnnotationControl/DeleteAnnotationControl';
import PlayAudioControl from '@/components/controls/PlayAudioControl/PlayAudioControl';
import CopyTextControl from '@/components/controls/CopyTextControl/CopyTextControl';
import BlogQuoteControl from '@/components/controls/BlogQuoteControl/BlogQuoteControl';
import CreateAnnotationControl from '@/components/controls/CreateAnnotationControl/CreateAnnotationControl';
import SocialSharingControl from '@/components/controls/SocialSharingControl/SocialSharingControl';
import TypoControl from '@/components/controls/TypoControl/TypoControl';
import SearchControl from '@/components/controls/SearchControl/SearchControl';
import ExtractControl from '@/components/controls/ExtractControl/ExtractControl';
import BookmarkControl from '@/components/controls/BookmarkControl/BookmarkControl';
import TranslateControl from '@/components/controls/TranslateControl/TranslateControl';
import LookupControl from '@/components/controls/LookupControl/LookupControl';
import ILMControl from '@/components/controls/ILMControl/ILMControl';
import HighlightControl from '@/components/controls/HighlightControl/HighlightControl';
import Tooltip from '@/components/views/Tooltip/Tooltip.vue';
import SlideGroup from '@/components/views/SlideGroup/SlideGroup.vue';
import TooltipFactory from '@/classes/factories/TooltipFactory';

import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import GoogleAnalyticsUtils from '@/services/utils/GoogleAnalyticsUtils';
import SelectionConstantUtils from '@shared/publication/selection/SelectionConstantUtils.mjs';

import ContextMenuTypesEnum from '@/enums/ContextMenuTypesEnum';
import ControlNamesEnum from '@/enums/ControlNamesEnum';
import MenuEventEnum from '@/enums/MenuEventEnum';
import BrandsEnum from '@shared/enums/BrandsEnum.mjs';

const logger = LoggerFactory.getLogger('ContextMenu.vue');

const READER_MENU_CLASS = 'reader-content';
const EDITOR_MENU_CLASS = 'editor-content';
const BORDER_BOTTOM_DISPLAY = 70;

const controlNamesByOrder = [
  ControlNamesEnum.HIGHLIGHT,
  ControlNamesEnum.EDIT_ANNOTATION,
  ControlNamesEnum.DELETE_ANNOTATION,
  ControlNamesEnum.TRANSLATE,
  ControlNamesEnum.LOOKUP,
  ControlNamesEnum.PLAY_AUDIO,
  ControlNamesEnum.CREATE_ANNOTATION,
  ControlNamesEnum.SEARCH,
  ControlNamesEnum.EXTRACT,
  ControlNamesEnum.BOOKMARK,
  ControlNamesEnum.COPY_TEXT,
  ControlNamesEnum.SOCIAL_SHARING,
  ControlNamesEnum.TYPO,
  ControlNamesEnum.ILM,
  ControlNamesEnum.BLOG_QUOTE
];

class MenuItem {
  constructor(buildData) {
    this.id = buildData.id;
    this.testId = buildData.testId;
    this.controlName = buildData.controlName;
    this.isVisible = buildData.isVisible;
    this.disabled = buildData.disabled || false;
    this.disableIcon = buildData.disableIcon || false;
  }
}

class MenuItemBuilder {
  setId(id) {
    this.id = id;
    return this;
  }

  setTestId(testId) {
    this.testId = testId;
    return this;
  }

  setControlName(name) {
    this.controlName = name;
    return this;
  }

  setDisabled(val) {
    this.disabled = val;
    return this;
  }

  setDisableIcon(val) {
    this.disableIcon = val;
    return this;
  }

  setIsVisible(val) {
    this.isVisible = val;
    return this;
  }

  build() {
    return new MenuItem(this);
  }
}

function $_createMenuItemByType(controlName, menuOpenContext) {
  let isVisible = false;
  let menuItemBuilder = new MenuItemBuilder();

  switch (controlName) {
    case ControlNamesEnum.HIGHLIGHT:
      menuItemBuilder
        .setId(ControlNamesEnum.HIGHLIGHT)
        .setTestId('context-menu-item highlightNote')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMultipleParaSelected)
        .setIsVisible(true);

      break;
    case ControlNamesEnum.EDIT_ANNOTATION:
      isVisible =
        !menuOpenContext.isGuest && this.getAnnotationFullSelectedByLocator;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.EDIT_NOTE)
        .setTestId('context-menu-item editNote')
        .setControlName(controlName)
        .setDisabled(this.isCategoriesNotExist)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.DELETE_ANNOTATION:
      isVisible =
        !menuOpenContext.isGuest && this.getAnnotationFullSelectedByLocator;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.DELETE)
        .setTestId('context-menu-item deleteNote')
        .setControlName(controlName)
        .setDisabled(!this.isCategoriesExist)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.TRANSLATE:
      isVisible =
        !this.readingSettingsView.dictionaryAutoOpen &&
        this.readingSettingsView.googleTranslate &&
        (menuOpenContext.isAdmin || menuOpenContext.isEditor);

      menuItemBuilder
        .setId(ContextMenuTypesEnum.TRANSLATE)
        .setTestId('context-menu-item translate')
        .setControlName(controlName)
        .setDisabled(!this.isCategoriesExist)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.LOOKUP:
      isVisible =
        !this.readingSettingsView.dictionaryAutoOpen &&
        (this.readingSettingsView.dictionaryMode ||
          this.readingSettingsView.wikiMode);

      menuItemBuilder
        .setId(ContextMenuTypesEnum.LOOKUP)
        .setTestId('context-menu-item wiki')
        .setControlName(controlName)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.PLAY_AUDIO:
      isVisible =
        !menuOpenContext.isCompilation &&
        !menuOpenContext.isEditor &&
        menuOpenContext.isElementVoiced &&
        menuOpenContext.hasAudio;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.PLAY)
        .setTestId('context-menu-item audio')
        .setControlName(controlName)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.CREATE_ANNOTATION:
      menuItemBuilder
        .setId(ContextMenuTypesEnum.NOTE)
        .setTestId('context-menu-item note')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMultipleParaSelected)
        .setIsVisible(true);
      break;

    case ControlNamesEnum.SEARCH:
      isVisible =
        !menuOpenContext.isCompilation &&
        menuOpenContext.brand === BrandsEnum.OCEAN;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.SEARCH)
        .setTestId('context-menu-item search')
        .setControlName(controlName)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.EXTRACT:
      isVisible =
        !menuOpenContext.isOffline &&
        !menuOpenContext.isHeader &&
        menuOpenContext.isReader;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.EXTRACT)
        .setTestId('context-menu-item compilation')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMaxParaSelected)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.BOOKMARK:
      menuItemBuilder
        .setId(ContextMenuTypesEnum.BOOKMARK)
        .setTestId('context-menu-item bookmark')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMultipleParaSelected)
        .setIsVisible(!menuOpenContext.isCompilation);
      break;

    case ControlNamesEnum.COPY_TEXT:
      menuItemBuilder
        .setId(ContextMenuTypesEnum.COPY)
        .setTestId('context-menu-item copy')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMaxParaSelected)
        .setIsVisible(true);
      break;

    case ControlNamesEnum.SOCIAL_SHARING:
      isVisible = menuOpenContext.isReader && !menuOpenContext.isOffline;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.SOCIAL_SHARING)
        .setTestId('context-menu-item socialSharing')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMaxParaSelected)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.TYPO:
      isVisible =
        !menuOpenContext.isOffline &&
        !menuOpenContext.isHeader &&
        menuOpenContext.isReader;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.REPORT)
        .setTestId('context-menu-item typo')
        .setControlName(controlName)
        .setDisableIcon(
          menuOpenContext.isMultipleParaSelected ||
            this.selectedLocator.endLocator.logicalCharOffset -
              this.selectedLocator.startLocator.logicalCharOffset >
              AppConstantsUtil.TYPO_MAX_SELECTED_SYMBOLS
        )
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.ILM:
      isVisible =
        menuOpenContext.isAdmin &&
        menuOpenContext.hasIlmId &&
        menuOpenContext.isDesktop &&
        !menuOpenContext.isDevice &&
        !menuOpenContext.isOffline;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.CORRECT)
        .setTestId('context-menu-item ilm')
        .setControlName(controlName)
        .setIsVisible(isVisible);
      break;

    case ControlNamesEnum.BLOG_QUOTE:
      isVisible = menuOpenContext.isAdmin || menuOpenContext.isUserEditor;

      menuItemBuilder
        .setId(ContextMenuTypesEnum.BLOG_QUOTE)
        .setTestId('context-menu-item blogQuote')
        .setControlName(controlName)
        .setDisableIcon(menuOpenContext.isMaxParaSelected)
        .setIsVisible(isVisible);
      break;

    default:
      logger.error(`Get unsupported ContextMenuTypes ${controlName}`);
      menuItemBuilder.setIsVisible(false);
      break;
  }

  let menuItem = menuItemBuilder.build();
  return menuItem;
}

export default {
  name: 'ContextMenu',
  components: {
    Tooltip,
    EditAnnotationControl,
    DeleteAnnotationControl,
    TranslateControl,
    LookupControl,
    PlayAudioControl,
    CreateAnnotationControl,
    SearchControl,
    ExtractControl,
    CopyTextControl,
    BlogQuoteControl,
    SocialSharingControl,
    TypoControl,
    BookmarkControl,
    ILMControl,
    HighlightControl,
    SlideGroup
  },
  props: {
    containerSelector: String,
    menuOpenContext: Object
  },
  data() {
    const appMode = this.$store.getters['ContextStore/appModeGetter'];
    const contextMenuTooltip = this.createContextMenuTooltip();
    const tooltip = TooltipFactory.createEmptyBottomInfoTooltip();
    const parentElement = document.querySelector(
      `.${AppConstantsUtil.SCROLL_LIST_CLASS}`
    );
    if (contextMenuTooltip) {
      tooltip.setAlign(contextMenuTooltip.getAlign());
    }

    const yOffset = this.getYOffset();

    return {
      appMode,
      parentElement,
      tooltip,
      contextMenuTooltip,
      activator: null,
      yOffset,
      xOffsetBorder: AppConstantsUtil.ANNOTATION_SIDEBAR_WIDTH
    };
  },
  computed: {
    bookId() {
      const openParams = this.$store.getters[
        'OpenParameterStore/getPublicationOpenParameters'
      ];
      return openParams?.publicationId;
    },
    selectedLocator() {
      return this.$store.getters['MaterialsStore/getSelectedLocator'];
    },
    readingSettingsView() {
      return this.$store.getters['ReadingSettingsStore/getReadingSettingsView'];
    },
    getAnnotationFullSelectedByLocator() {
      return this.$store.getters[
        'AnnotationsStore/getAnnotationFullSelectedByLocator'
      ](this.selectedLocator, this.bookId);
    },
    isCategoriesExist() {
      return this.$store.getters['CategoriesStore/isCategoriesExist'];
    },
    menuItems() {
      const menuItems = controlNamesByOrder.reduce(
        (visibleMenuItems, controlName) => {
          const menuItem = this.$_createMenuItemByType(
            controlName,
            this.menuOpenContext
          );
          if (menuItem.isVisible) {
            visibleMenuItems.push(menuItem);
          }
          return visibleMenuItems;
        },
        []
      );
      return menuItems;
    },
    isDevice() {
      return this.$store.getters['ContextStore/isDevice'];
    },
    shownIcons() {
      return this.menuOpenContext.isReader;
    },
    shownLabels() {
      return this.menuOpenContext.isEditor;
    },
    contextMenuClass() {
      return this.shownIcons ? READER_MENU_CLASS : EDITOR_MENU_CLASS;
    },
    isOcean() {
      const siteBrand = this.$store.getters['ContextStore/brand'];
      return siteBrand === BrandsEnum.OCEAN;
    },
    isGuest() {
      return this.menuOpenContext.isGuest;
    },
    isGuestInOcean() {
      return this.isOcean && this.isGuest;
    }
  },
  watch: {
    selectedLocator() {
      this.contextMenuTooltip = this.createContextMenuTooltip();
      this.yOffset = this.getYOffset();
    }
  },
  mounted() {
    try {
      this.$_showMenu();
    } catch (error) {
      this.$_contextMenuErrorHandler(error);
    }
  },
  destroyed() {
    this.close();
  },
  methods: {
    close() {
      this.$emit('menuEvent', {
        type: MenuEventEnum.CLOSE,
        data: {}
      });
    },
    menuIconClickHandler(e) {
      if (!e.target?.closest('.close-prevent') || this.isGuest) {
        this.close();
      }
    },
    controlEventHandler({ type, data }) {
      switch (type) {
        case MenuEventEnum.REOPEN: {
          this.$emit('menuEvent', {
            type: MenuEventEnum.REOPEN,
            data
          });
          this.close();
          break;
        }
      }
    },
    $_getDisplayElement() {
      return document.querySelector(this.containerSelector);
    },
    createContextMenuTooltip() {
      const displayElement = this.$_getDisplayElement();
      if (!displayElement) {
        return null;
      }
      const selections = displayElement.querySelectorAll(
        `.${SelectionConstantUtils.SELECTIONS_CLASS}`
      );
      if (!selections?.length) {
        return null;
      }

      let selection = selections[selections.length - 1];
      const { top } = selection.getBoundingClientRect();
      let selectionTop = top;
      if (
        selectionTop >
        window.innerHeight - SelectionConstantUtils.PROGRESS_TOOLBAR_HEIGHT
      ) {
        selection = selections[0];
        selectionTop = selection.getBoundingClientRect().top;
      }

      const positionRatio = Math.round(
        (selectionTop / window.innerHeight) * 100
      );
      let contextMenuTooltip =
        positionRatio < BORDER_BOTTOM_DISPLAY
          ? TooltipFactory.createEmptyBottomControllTooltip()
          : TooltipFactory.createEmptyTopControllTooltip();
      contextMenuTooltip.setElement(selection);

      return contextMenuTooltip;
    },
    showTooltip(event, menuItem) {
      if (this.isDevice) {
        return;
      }
      this.tooltip.setElement(event.currentTarget);
      this.tooltip.setTooltipText(menuItem.label);
    },
    hideTooltip() {
      this.tooltip.setElement(null);
    },
    getYOffset() {
      const scrollElement = this.$store.getters[
        'PublicationStore/getScrollElement'
      ];
      if (!scrollElement) {
        return 0;
      }
      const scrollTop = scrollElement.scrollTop;
      const paddingTop = window.getComputedStyle(scrollElement, null)
        .paddingTop;
      return scrollTop - parseFloat(paddingTop);
    },
    $_showMenu() {
      setTimeout(() => {
        this.$_onShowMenu();
      });
    },
    $_onShowMenu() {
      setTimeout(() => {
        const contextDialog = this.$_getPopupElement();
        if (!contextDialog) {
          return false;
        }
        const dialogClientRect = contextDialog.getBoundingClientRect();
        this.$emit('menuEvent', {
          type: MenuEventEnum.READY_ELEMENT_POSITION,
          data: {
            top: dialogClientRect.top
          }
        });
      }, 300);
    },
    $_getPopupElement() {
      return window.document.querySelector('.context-menu-content');
    },
    $_createMenuItemByType,
    $_playAudioHandler() {
      GoogleAnalyticsUtils.trackEvent('book', 'contextMenu', 'play');
      this.$emit('menuEvent', {
        type: MenuEventEnum.PLAY_AUDIO
      });
    },
    $_callCreateReport() {
      this.$emit('menuEvent', {
        type: MenuEventEnum.CREATE_REPORT,
        payload: {
          // locator: this.locator
        }
      });
    },
    $_openCreateCompilationPopup() {
      this.$emit('menuEvent', {
        type: MenuEventEnum.ADD_SELECTION_TO_COMPILATION,
        payload: {
          data: {
            locator: this.selectedLocator
          }
        }
      });
    },
    $_contextMenuErrorHandler(error) {
      logger.fatal(`Error in Context menu: ${error.stack}`);
      this.$emit('menuEvent', {
        type: MenuEventEnum.ERROR,
        data: { error }
      });
    }
  }
};
</script>

<style lang="less" src="./ContextMenu.less"></style>
